import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import type {
  ItemDoChecklist,
  SituacaoDoChecklist,
} from '@/modulos/portal-credenciado/comum/contrato';
import { chamarApi, type ErroApi } from '@/nucleo/auth/cliente-api';
import { mascaraPlaca } from '@/nucleo/formato/documentos';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

import {
  API_DOCUMENTOS,
  type DocumentoEnviado,
  type Envelope,
  type EnvioDeDocumento,
  type EstadoDosDocumentos,
  type ItemDaApi,
  type Natureza,
  type SituacaoDoDocumento,
} from './contrato';

/**
 * Camada de dados da tela de documentos do credenciado. Dado real, da API
 * `bff/v1/portal-credenciado-documentos`; de quem é o cadastro decide o banco,
 * pelo usuário do token (0279). Nenhum mock.
 *
 * O cabeçalho `X-Credenciado-Id` não é enviado: o front ainda não tem seletor
 * de cadastro, e sem ele a API usa o cadastro do perfil — a mesma escolha da
 * Minha Inscrição e de Meus Veículos.
 */

export const CHAVES_DE_DOCUMENTOS = {
  lista: ['credenciado', 'documentos', 'lista'] as const,
};

/** Maior página que a API aceita; a tela lê todas, porque agrupa por recurso. */
const TAMANHO_DA_PAGINA = 100;

async function lerTudo(): Promise<EstadoDosDocumentos> {
  const pagina = (n: number) =>
    chamarApi<Envelope<EstadoDosDocumentos>>(
      `${API_DOCUMENTOS}/state?pagina=${n}&tamanho=${TAMANHO_DA_PAGINA}`,
    ).then((r) => r.state);
  const primeira = await pagina(1);
  const itens = [...primeira.itens];
  for (let n = 2; itens.length < primeira.total; n++) {
    const seguinte = await pagina(n);
    if (seguinte.itens.length === 0) break;
    itens.push(...seguinte.itens);
  }
  return { ...primeira, itens };
}

export function useDocumentos(): UseQueryResult<EstadoDosDocumentos, ErroApi> {
  return useQuery<EstadoDosDocumentos, ErroApi>({
    queryKey: CHAVES_DE_DOCUMENTOS.lista,
    queryFn: lerTudo,
    retry: naoInsistirEmRecusa,
  });
}

/** Envio e reenvio. A tela relê a lista inteira no sucesso, sem montar o item na mão. */
export function useEnviarDocumento() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: EnvioDeDocumento) =>
      chamarApi<Envelope<{ documento: DocumentoEnviado }>>(`${API_DOCUMENTOS}/documentos`, {
        metodo: 'POST',
        corpo,
      }).then((r) => r.state.documento),
    onSuccess: () => cliente.invalidateQueries({ queryKey: CHAVES_DE_DOCUMENTOS.lista }),
  });
}

/** Situação da API → situação do `DocumentChecklist`. "Em análise" é o "Enviado" do protótipo. */
export const SITUACAO_NA_TELA: Record<SituacaoDoDocumento, SituacaoDoChecklist> = {
  PENDENTE: 'pendente',
  EM_ANALISE: 'enviado',
  APROVADO: 'aprovado',
  REJEITADO: 'rejeitado',
  VENCIDO: 'vencido',
};

/** Situações em que a validade foi conferida pelo DTP (aprovado, e aprovado que venceu). */
const VALIDADE_CONFERIDA: ReadonlySet<SituacaoDoDocumento> = new Set(['APROVADO', 'VENCIDO']);

/** Identificador da linha: o mesmo requisito aparece em cada veículo e colaborador. */
export function idDoItem(item: ItemDaApi): string {
  return `${item.natureza}:${item.recursoId}:${item.requisito.codigo}`;
}

export function paraOChecklist(item: ItemDaApi): ItemDoChecklist {
  return {
    id: idDoItem(item),
    nome: item.requisito.titulo,
    descricao: item.requisito.descricao,
    obrigatorio: item.requisito.obrigatorio,
    situacao: SITUACAO_NA_TELA[item.situacao],
    documento: item.documento
      ? {
          arquivoNome: item.documento.arquivoNome,
          // O selo de validade só vale para o que o DTP aprovou: em análise ou
          // rejeitado, a data é a que o credenciado informou, e "Vigente" ali
          // afirmaria uma validade que ninguém conferiu.
          validadeAte: VALIDADE_CONFERIDA.has(item.situacao) ? item.documento.venceEm : null,
        }
      : null,
    podeEnviar: item.podeEnviar,
  };
}

/** Um cartão da tela: a empresa, um veículo, um condutor ou um monitor. */
export interface GrupoDeDocumentos {
  chave: string;
  natureza: Natureza;
  titulo: string;
  subtitulo: string | null;
  itens: ItemDaApi[];
  /** Algum documento pede ação (Pendente, Rejeitado ou Vencido): o cartão abre. */
  temPendencia: boolean;
}

const ORDEM: Record<Natureza, number> = { EMPRESA: 0, VEICULO: 1, CONDUTOR: 2, MONITOR: 3 };

function tituloDoGrupo(
  item: ItemDaApi,
  coletivo: boolean,
): { titulo: string; subtitulo: string | null } {
  const r = item.recurso;
  switch (item.natureza) {
    case 'EMPRESA':
      return { titulo: coletivo ? 'Empresa' : 'Meus documentos', subtitulo: null };
    case 'VEICULO': {
      const modelo = [r.marca, r.modelo].filter(Boolean).join(' ').trim();
      return { titulo: mascaraPlaca(r.placa) || 'Veículo', subtitulo: modelo || null };
    }
    case 'CONDUTOR':
      return { titulo: `Condutor · ${r.nome ?? ''}`.trim(), subtitulo: r.cpfMascarado ?? null };
    default:
      return { titulo: `Monitor · ${r.nome ?? ''}`.trim(), subtitulo: r.cpfMascarado ?? null };
  }
}

/**
 * Agrupa os itens em cartões, na ordem Empresa, Veículos, Condutores,
 * Monitores (decisão F1 do plano de front). Um cartão para cada recurso que a
 * API manda, sem filtro aqui: o que aparece é o que o resumo do topo conta.
 *
 * Pessoa física: a API não manda o condutor que é o próprio titular (0279,
 * seção 8), então quem dirige o próprio veículo não vê cartão de condutor; o
 * condutor preposto vem, e aparece com a CNH e o vínculo dele (decisão do dono
 * de 02/10/2026).
 */
export function montarGrupos(itens: ItemDaApi[], coletivo: boolean): GrupoDeDocumentos[] {
  const grupos = new Map<string, GrupoDeDocumentos>();
  for (const item of itens) {
    const chave = `${item.natureza}:${item.recursoId}`;
    let grupo = grupos.get(chave);
    if (!grupo) {
      grupo = {
        chave,
        natureza: item.natureza,
        ...tituloDoGrupo(item, coletivo),
        itens: [],
        temPendencia: false,
      };
      grupos.set(chave, grupo);
    }
    grupo.itens.push(item);
    if (
      item.situacao === 'PENDENTE' ||
      item.situacao === 'REJEITADO' ||
      item.situacao === 'VENCIDO'
    ) {
      grupo.temPendencia = true;
    }
  }
  // Ordenação estável: dentro da natureza, a ordem que a API mandou.
  return [...grupos.values()].sort((a, b) => ORDEM[a.natureza] - ORDEM[b.natureza]);
}

/**
 * Resumo do topo (F11), com a frase do Rafael: "3 documentos pendentes ou
 * vencidos"; nada quando não há pendência. Conta os obrigatórios em Pendente,
 * Rejeitado ou Vencido (`contagem.pendenciasObrigatorias` da API) — proposta
 * P5 do plano de front, a confirmar com ele.
 */
export function textoDoResumo(pendencias: number): string | null {
  if (pendencias <= 0) return null;
  return pendencias === 1
    ? '1 documento pendente ou vencido'
    : `${pendencias} documentos pendentes ou vencidos`;
}
